import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { FormTemplatesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as FormTemplateActions from "../../../actions/formTemplates";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { QALookupsLayout } from "../../index";

import "./FormTemplatesPage.scss";

interface FormTemplatesPageProps {
    formTemplates: Dtos.FormTemplate[];
    formFilter: IFormFilter;
    loadingFormTemplates: boolean;
    loadFormTemplatesSuccess: boolean;
    loadFormTemplatesFailure: boolean;
    loadFormTemplatesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearFormTemplates: FormTemplateActions.IFormTemplateClearActionCreator;
    loadFormTemplates: FormTemplateActions.IFormTemplateLoadActionCreator;
    filter: FormTemplateActions.IFormTemplateFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const FormTemplatesPage: FC<FormTemplatesPageProps> = ({
    formTemplates,
    formFilter,
    loadingFormTemplates,
    loadFormTemplatesSuccess,
    loadFormTemplatesFailure,
    loadFormTemplatesErrorCode,
    permissions,
    pageSize,
    clearFormTemplates,
    loadFormTemplates,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showFormTemplates = !loadingFormTemplates && loadFormTemplatesSuccess;


    // Load form templates
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(FormTemplatesBreadcrumbs());

            loadFormTemplates();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        FormTemplatesBreadcrumbs,
        loadFormTemplates,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadFormTemplatesFailure) {
            navigate(`/error/${loadFormTemplatesErrorCode}`);
        }
    }, [loadFormTemplatesFailure, loadFormTemplatesErrorCode, navigate]);

    // Cleanup effect to clear the form templates on component unmount
    useEffect(() => {
        return () => {
            clearFormTemplates();
        };
    }, [clearFormTemplates]);

    const createTable = useMemo(() => {
        if (!formTemplates || formTemplates.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no form templates to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = formTemplates.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={formTemplates}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : formTemplates.length
                    }
                    pageSize={showPagination ? pageSize : formTemplates.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no form templates to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/qa/formtemplates/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                    ]}
                />
            </div>
        );
    }, [formTemplates, PAGE_SIZE_OPTIONS, pageSize, navigate, setPageSize]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Form Templates</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.FormTemplatesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/qa/formtemplates/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showFormTemplates ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showFormTemplates ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showFormTemplates, createTable]);

    return (
        <QALookupsLayout
            loading={loadingFormTemplates}
            permission={Dtos.Permission.FormTemplatesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<FormTemplatesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formTemplates: state.formTemplates.formFilteredData!,
        formFilter: state.formTemplates.formFilter,
        loadingFormTemplates:
            state.formTemplates.loadState &&
            state.formTemplates.loadState.status == RequestState.Pending,
        loadFormTemplatesSuccess:
            state.formTemplates.loadState &&
            state.formTemplates.loadState.status == RequestState.Success,
        loadFormTemplatesFailure:
            state.formTemplates.loadState &&
            state.formTemplates.loadState.status == RequestState.Failure,
        loadFormTemplatesErrorCode: state.formTemplates.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<FormTemplatesPageProps> => {
    return {
        loadFormTemplates: bindActionCreators(
            FormTemplateActions.LoadFormTemplate,
            dispatch
        ),
        clearFormTemplates: bindActionCreators(
            FormTemplateActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(FormTemplateActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FormTemplatesPage);
